import Highcharts from 'highcharts';

/**
 * Get chart canvas from Highcharts chart instance
 * @returns Hook to generate svg canvas from chart
 */
export const useGetChartCanvas = () => {
  return (chart: Highcharts.Chart): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      try {
        // Create a canvas element to render the SVG
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Create an image element to load the SVG data
        const img = new Image();
        const svgBlob = new Blob(
          [
            chart.exporting.getSVG(
              Highcharts.merge(chart.options.exporting?.chartOptions, {
                // Render the export at the widget's live on-screen size.
                // Without this, getSVG falls back to Highcharts' default
                // 600x400 (the responsive chart sets no chart.width/height
                // and the container's percentage height fails getSVG's
                // `px`-only sniff), so a tall widget's axis labels and
                // stacked data-label tags get clipped (RSP-4105).
                chart: {
                  width: chart.chartWidth,
                  height: chart.chartHeight,
                },
              })
            ),
          ],
          {
            type: 'image/svg+xml;charset=utf-8',
          }
        );
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          // Set canvas dimensions to match the image
          canvas.width = img.width || chart.chartWidth || 800;
          canvas.height = img.height || chart.chartHeight || 600;

          if (!ctx) {
            URL.revokeObjectURL(url);
            reject('Unable to get canvas context');

            return;
          }

          // Draw the SVG image onto the canvas
          ctx.drawImage(img, 0, 0);

          URL.revokeObjectURL(url);

          resolve(canvas);
        };

        img.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(error);
        };

        img.src = url;
      } catch (error) {
        reject(error);
      }
    });
  };
};
